import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const JOURNEY_STATUS_LABEL = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  unsubscribed: 'Unsubscribed',
};

export default function WelcomeSequence() {
  const [state, setState] = useState({ steps: [], enabled: true, stats: {} });
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // step being edited
  const [previewHtml, setPreviewHtml] = useState(null);
  const [journeys, setJourneys] = useState({ items: [], total: 0 });
  const [journeyStatus, setJourneyStatus] = useState('all');
  const [journeySearch, setJourneySearch] = useState('');
  const [journeyDetail, setJourneyDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminApi.get('/admin/welcome-sequence').then((r) => r.data);
      setState(d);
    } catch (e) {
      toast.error('Unable to load sequence.');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const d = await adminApi.get('/admin/welcome-sequence/analytics').then((r) => r.data);
      setAnalytics(d);
    } catch (_) { /* ignore */ }
  };

  const loadJourneys = async (status = journeyStatus, q = journeySearch) => {
    try {
      const params = {};
      if (status && status !== 'all') params.status = status;
      if (q && q.trim()) params.q = q.trim();
      const d = await adminApi.get('/admin/welcome-sequence/journeys', { params }).then((r) => r.data);
      setJourneys(d);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { load(); loadJourneys(); loadAnalytics(); }, []);
  useEffect(() => { loadJourneys(journeyStatus, journeySearch); }, [journeyStatus]);

  // Debounce the search input
  useEffect(() => {
    const t = setTimeout(() => loadJourneys(journeyStatus, journeySearch), 300);
    return () => clearTimeout(t);
  }, [journeySearch]);

  const toggleGlobal = async () => {
    try {
      const next = !state.enabled;
      await adminApi.post('/admin/welcome-sequence/settings', { enabled: next });
      toast.success(next ? 'Sequence is now active.' : 'Sequence paused. No new sends will fire.');
      await load();
    } catch (e) {
      toast.error('Update failed.');
    }
  };

  const toggleStep = async (step) => {
    try {
      await adminApi.patch(`/admin/welcome-sequence/steps/${step.id}`, { is_active: !step.is_active });
      toast.success(step.is_active ? 'Step paused.' : 'Step activated.');
      await load();
    } catch (e) {
      toast.error('Update failed.');
    }
  };

  const move = async (step, direction) => {
    const idx = state.steps.findIndex((s) => s.id === step.id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= state.steps.length) return;
    const newOrder = [...state.steps];
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    try {
      await adminApi.post('/admin/welcome-sequence/reorder', { order: newOrder.map((s) => s.id) });
      await load();
    } catch (e) {
      toast.error('Reorder failed.');
    }
  };

  const testSend = async (step) => {
    try {
      const r = await adminApi.post(`/admin/welcome-sequence/steps/${step.id}/test-send`).then((r) => r.data);
      toast.success(`Test email sent to ${r.sent_to}`);
    } catch (e) {
      toast.error('Test send failed.');
    }
  };

  const preview = async (step) => {
    try {
      const r = await adminApi.get(`/admin/welcome-sequence/steps/${step.id}/preview`).then((r) => r.data);
      setPreviewHtml(r.html);
    } catch (e) {
      toast.error('Preview failed.');
    }
  };

  const addStep = async () => {
    try {
      const nextDay = (state.steps[state.steps.length - 1]?.day_offset || 0) + 3;
      const r = await adminApi.post('/admin/welcome-sequence/steps', {
        title: 'New Letter',
        subject: '',
        body_html: '<p>Write your letter here.</p>',
        day_offset: nextDay,
        is_active: false,
      }).then((r) => r.data);
      await load();
      setEditor(r.step);
    } catch (e) {
      toast.error('Could not add step.');
    }
  };

  const deleteStep = async (step) => {
    if (!window.confirm(`Delete "${step.title}"? This can't be undone. Existing journeys already past this step aren't affected.`)) return;
    try {
      await adminApi.delete(`/admin/welcome-sequence/steps/${step.id}`);
      toast.success('Step deleted.');
      await load();
    } catch (e) {
      toast.error('Delete failed.');
    }
  };

  const pauseJourney = async (j) => {
    try {
      await adminApi.post(`/admin/welcome-sequence/journeys/${j.id}/pause`);
      toast.success('Journey paused.');
      await loadJourneys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pause failed.');
    }
  };

  const resumeJourney = async (j) => {
    try {
      await adminApi.post(`/admin/welcome-sequence/journeys/${j.id}/resume`);
      toast.success('Journey resumed.');
      await loadJourneys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed.');
    }
  };

  const openJourney = async (j) => {
    try {
      const d = await adminApi.get(`/admin/welcome-sequence/journeys/${j.id}`).then((r) => r.data);
      setJourneyDetail(d);
    } catch (e) {
      toast.error('Could not load journey.');
    }
  };

  return (
    <div data-testid="admin-welcome-sequence-view">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Founder&rsquo;s List</div>
          <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">
            Welcome Sequence
          </h1>
          <p className="mt-3 text-[13px] text-neutral-500 max-w-2xl">
            The permanent onboarding journey every new Founder&rsquo;s List subscriber receives. Editable, reorderable, and independent from the weekly Founder&rsquo;s Brief.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.2em] ${state.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-200 text-neutral-600'}`}>
            {state.enabled ? 'Sequence Active' : 'Sequence Paused'}
          </div>
          <button
            onClick={toggleGlobal}
            data-testid="welcome-sequence-toggle-global"
            className={`uppercase tracking-[0.22em] text-[10px] px-5 py-3 rounded-full ${state.enabled ? 'border border-neutral-300 text-neutral-700 hover:bg-neutral-100' : 'bg-neutral-900 text-white hover:bg-[#1a1a1a]'}`}
          >
            {state.enabled ? 'Pause Sequence' : 'Activate Sequence'}
          </button>
        </div>
      </header>

      {/* Sequence Performance / Analytics */}
      <SequencePerformance data={analytics} />

      {/* Steps */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Sequence Steps</div>
          <button
            onClick={addStep}
            data-testid="welcome-sequence-add-step"
            className="uppercase tracking-[0.22em] text-[10px] px-4 py-2 border border-neutral-300 rounded-full text-neutral-700 hover:bg-neutral-100"
          >
            + Add Step
          </button>
        </div>

        <div className="space-y-3">
          {loading && (
            <div className="text-center py-10 uppercase tracking-[0.2em] text-[11px] text-neutral-500">Loading…</div>
          )}
          {!loading && state.steps.length === 0 && (
            <div className="text-center py-10 text-neutral-500 text-[13px]">No steps yet. Click &ldquo;+ Add Step&rdquo; to create the first one.</div>
          )}
          {!loading && state.steps.map((s, idx) => (
            <article
              key={s.id}
              data-testid={`welcome-step-row-${s.order}`}
              className={`bg-white border rounded-xl p-5 transition-all ${s.is_active ? 'border-neutral-200/60' : 'border-neutral-200/60 opacity-60'}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                <div className="flex md:flex-col md:items-center gap-4 md:gap-1 md:w-16 shrink-0 mb-3 md:mb-0">
                  <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl text-[#C8A15A]">
                    {String(s.order).padStart(2, '0')}
                  </div>
                  <div className="flex md:flex-col gap-1">
                    <button
                      onClick={() => move(s, 'up')}
                      disabled={idx === 0}
                      className="w-6 h-6 leading-none text-neutral-500 hover:text-neutral-900 disabled:opacity-30"
                      aria-label="Move up"
                    >↑</button>
                    <button
                      onClick={() => move(s, 'down')}
                      disabled={idx === state.steps.length - 1}
                      className="w-6 h-6 leading-none text-neutral-500 hover:text-neutral-900 disabled:opacity-30"
                      aria-label="Move down"
                    >↓</button>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-[13px] text-neutral-900 font-medium truncate">{s.title || '(untitled)'}</div>
                    {!s.is_active && (
                      <span className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 border border-neutral-300 rounded-full px-2 py-0.5">Paused</span>
                    )}
                  </div>
                  <div className="text-[12px] text-neutral-500 mb-2 line-clamp-1">Subject: {s.subject || '—'}</div>
                  <div className="text-[11px] text-neutral-500">
                    Sends <span className="text-neutral-800">Day {s.day_offset}</span> after subscribe
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-3 md:mt-0 shrink-0">
                  <button
                    onClick={() => setEditor(s)}
                    data-testid={`welcome-step-edit-${s.order}`}
                    className="uppercase tracking-[0.18em] text-[10px] px-3 py-2 border border-neutral-300 rounded-full text-neutral-700 hover:bg-neutral-100"
                  >Edit</button>
                  <button
                    onClick={() => preview(s)}
                    className="uppercase tracking-[0.18em] text-[10px] px-3 py-2 border border-neutral-300 rounded-full text-neutral-700 hover:bg-neutral-100"
                  >Preview</button>
                  <button
                    onClick={() => testSend(s)}
                    className="uppercase tracking-[0.18em] text-[10px] px-3 py-2 border border-neutral-300 rounded-full text-neutral-700 hover:bg-neutral-100"
                  >Test Send</button>
                  <button
                    onClick={() => toggleStep(s)}
                    className={`uppercase tracking-[0.18em] text-[10px] px-3 py-2 rounded-full ${s.is_active ? 'text-neutral-700 hover:bg-neutral-100 border border-neutral-300' : 'bg-neutral-900 text-white hover:bg-[#1a1a1a]'}`}
                  >{s.is_active ? 'Pause' : 'Activate'}</button>
                  <button
                    onClick={() => deleteStep(s)}
                    className="uppercase tracking-[0.18em] text-[10px] px-3 py-2 text-red-600 hover:bg-red-50 rounded-full"
                  >Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Journeys */}
      <section>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Subscriber Journeys</div>
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative">
              <input
                type="text"
                value={journeySearch}
                onChange={(e) => setJourneySearch(e.target.value)}
                placeholder="Search by name or email…"
                data-testid="welcome-journey-search"
                className="bg-white border border-neutral-200 rounded-full pl-9 pr-4 py-2 text-[13px] focus:outline-none focus:border-neutral-900 w-full md:w-64"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-[12px]">⌕</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'active', 'completed', 'paused', 'unsubscribed'].map((s) => (
                <button
                  key={s}
                  onClick={() => setJourneyStatus(s)}
                  className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${journeyStatus === s ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200/60 rounded-xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50/70">
              <tr>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Subscriber</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Progress</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Status</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Next Send</th>
                <th className="text-right px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/60">
              {journeys.items.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500">
                  {journeySearch ? `No journeys matching "${journeySearch}".` : 'No journeys in this view yet.'}
                </td></tr>
              )}
              {journeys.items.map((j) => (
                <tr key={j.id} className="hover:bg-neutral-50/70">
                  <td
                    className="px-5 py-3 cursor-pointer"
                    onClick={() => openJourney(j)}
                    data-testid={`welcome-journey-row-${j.email}`}
                  >
                    <div className="text-neutral-900">{j.first_name || '—'}</div>
                    <div className="text-neutral-500 text-[11px]">{j.email}</div>
                  </td>
                  <td className="px-5 py-3 text-neutral-700">
                    {(j.sent_step_ids?.length ?? 0)} of {state.steps.length} sent
                  </td>
                  <td className="px-5 py-3">
                    <span className={`uppercase tracking-[0.18em] text-[10px] ${
                      j.status === 'active' ? 'text-emerald-700' :
                      j.status === 'completed' ? 'text-neutral-500' :
                      j.status === 'paused' ? 'text-[#C8A15A]' : 'text-red-600'
                    }`}>{JOURNEY_STATUS_LABEL[j.status] || j.status}</span>
                  </td>
                  <td className="px-5 py-3 text-neutral-500 text-[11px]">
                    {j.next_send_at ? new Date(j.next_send_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openJourney(j)}
                      className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 hover:text-neutral-900 mr-3"
                    >View</button>
                    {j.status === 'active' && (
                      <button onClick={() => pauseJourney(j)} className="text-[10px] uppercase tracking-[0.18em] text-neutral-600 hover:text-neutral-900">Pause</button>
                    )}
                    {j.status === 'paused' && (
                      <button onClick={() => resumeJourney(j)} className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 hover:text-emerald-900">Resume</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editor && (
        <StepEditor
          step={editor}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); loadAnalytics(); }}
          onPreview={setPreviewHtml}
        />
      )}
      {previewHtml && <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />}
      {journeyDetail && (
        <JourneyDetailModal
          detail={journeyDetail}
          steps={state.steps}
          onClose={() => setJourneyDetail(null)}
        />
      )}
    </div>
  );
}

function StepEditor({ step, onClose, onSaved, onPreview }) {
  const [form, setForm] = useState({
    title: step.title || '',
    subject: step.subject || '',
    preview_text: step.preview_text || '',
    day_offset: step.day_offset ?? 0,
    body_html: step.body_html || '',
    featured_image_url: step.featured_image_url || '',
    is_active: !!step.is_active,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/welcome-sequence/steps/${step.id}`, {
        ...form,
        day_offset: Math.max(0, parseInt(form.day_offset, 10) || 0),
      });
      toast.success('Step saved.');
      onSaved();
    } catch (e) {
      toast.error('Save failed.');
    } finally { setSaving(false); }
  };

  const preview = async () => {
    // Save first so preview reflects the edits
    await adminApi.patch(`/admin/welcome-sequence/steps/${step.id}`, {
      ...form,
      day_offset: Math.max(0, parseInt(form.day_offset, 10) || 0),
    });
    const r = await adminApi.get(`/admin/welcome-sequence/steps/${step.id}/preview`).then((r) => r.data);
    onPreview(r.html);
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-neutral-900/30 backdrop-blur-sm" />
      <aside
        data-testid="welcome-sequence-editor-drawer"
        className="fixed right-0 top-0 z-50 h-full w-full lg:w-[720px] bg-[#FAF8F5] border-l border-neutral-200/70 overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-[#FAF8F5]/95 backdrop-blur border-b border-neutral-200/60 px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="uppercase tracking-[0.24em] text-[10px] text-neutral-500 mb-1">
              Letter {step.order} · Day {form.day_offset} · {form.is_active ? 'Active' : 'Paused'}
            </div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-xl text-neutral-900">
              {form.title || '(untitled)'}
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-2xl leading-none px-2" aria-label="Close">×</button>
        </div>

        <div className="px-6 lg:px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Internal Title" value={form.title} onChange={(v) => set('title', v)} />
            <Field label="Day Offset (days after subscribe)" value={form.day_offset} onChange={(v) => set('day_offset', v)} type="number" />
            <Field label="Subject Line" value={form.subject} onChange={(v) => set('subject', v)} />
            <Field label="Preview Text (optional)" value={form.preview_text} onChange={(v) => set('preview_text', v)} />
          </div>

          <div>
            <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">
              Featured Image URL (optional · shown at top of email)
            </label>
            <input
              type="text"
              value={form.featured_image_url}
              onChange={(e) => set('featured_image_url', e.target.value)}
              placeholder="https://iamtoiawallace.com/images/..."
              className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900"
            />
            {form.featured_image_url && (
              <div className="mt-3 rounded-lg overflow-hidden border border-neutral-200 max-w-md">
                <img
                  src={form.featured_image_url}
                  alt="Featured"
                  className="w-full h-auto block"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-2">Use a full HTTPS URL. Leave blank for a text-only editorial letter.</p>
          </div>

          <div>
            <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">
              Body (HTML allowed · use <code className="text-[10px] bg-neutral-100 px-1 py-0.5 rounded">{'{{FIRST_NAME}}'}</code> and <code className="text-[10px] bg-neutral-100 px-1 py-0.5 rounded">{'{{SITE_URL}}'}</code>)
            </label>
            <textarea
              rows={16}
              value={form.body_html}
              onChange={(e) => set('body_html', e.target.value)}
              className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-neutral-900"
              placeholder="<p>Write your editorial letter here…</p>"
            />
          </div>

          <label className="flex items-center gap-3 text-[13px] text-neutral-700">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              className="w-4 h-4 accent-neutral-900"
            />
            Send this letter to new subscribers (uncheck to pause without deleting)
          </label>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-neutral-200/60">
            <button
              onClick={save}
              disabled={saving}
              data-testid="welcome-step-save"
              className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-5 py-3 rounded-full hover:bg-[#1a1a1a] disabled:opacity-50"
            >{saving ? 'Saving…' : 'Save'}</button>
            <button
              onClick={preview}
              className="uppercase tracking-[0.2em] text-[10px] px-5 py-3 border border-neutral-300 rounded-full hover:bg-neutral-100"
            >Preview</button>
          </div>
        </div>
      </aside>
    </>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900"
      />
    </div>
  );
}

function PreviewModal({ html, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-neutral-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-[720px] w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-neutral-200/60 px-5 py-3 flex justify-between items-center">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Preview</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-xl px-2" aria-label="Close">×</button>
        </div>
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}

function SequencePerformance({ data }) {
  if (!data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {['Total Enrolled', 'Active', 'Completed', 'Avg Completion Rate'].map((label) => (
          <div key={label} className="bg-white border border-neutral-200/60 rounded-xl px-5 py-4 animate-pulse">
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">{label}</div>
            <div className="text-3xl text-neutral-300 tabular-nums">—</div>
          </div>
        ))}
      </div>
    );
  }
  const { summary, per_step, time_series, webhook_configured } = data;
  const maxOpens = Math.max(1, ...per_step.map((s) => s.opened));
  const maxClicks = Math.max(1, ...per_step.map((s) => s.clicked));

  return (
    <div data-testid="welcome-sequence-performance" className="mb-12">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Enrolled', value: summary.total_enrolled },
          { label: 'Active Journeys', value: summary.active },
          { label: 'Completed', value: summary.completed },
          { label: 'Avg Completion', value: `${summary.avg_completion_rate}%` },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-neutral-200/60 rounded-xl px-5 py-4">
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">{s.label}</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl text-neutral-900 tabular-nums">
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Webhook status banner */}
      {!webhook_configured && (
        <div className="mb-6 p-4 bg-[#C8A15A]/10 border border-[#C8A15A]/40 rounded-lg text-[12px] text-neutral-700">
          <div className="uppercase tracking-[0.2em] text-[10px] text-[#8f6e2c] mb-1">Analytics setup</div>
          Open, click, and bounce rates require a Resend webhook. In Resend → Webhooks, add endpoint&nbsp;
          <code className="text-[11px] bg-white/60 px-1.5 py-0.5 rounded">{`${window.location.origin}/api/webhooks/resend`}</code>
          &nbsp;subscribe to <em>email.delivered / opened / clicked / bounced / complained</em>, and paste the signing secret into <code className="text-[11px] bg-white/60 px-1.5 py-0.5 rounded">RESEND_WEBHOOK_SECRET</code> in backend .env. Delivery totals ({summary.total_enrolled} enrolled) still track without it.
        </div>
      )}

      {/* Per-step metrics table */}
      <div className="bg-white border border-neutral-200/60 rounded-xl overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-neutral-200/60 flex items-center justify-between">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Per-Email Performance</div>
          <div className="uppercase tracking-[0.22em] text-[9px] text-neutral-400 lg:hidden">← swipe →</div>
        </div>
        <div className="overflow-x-auto relative">
          <table className="w-full text-[12px] min-w-[900px]">
            <thead className="bg-neutral-50/70">
              <tr>
                <th className="text-left px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">#</th>
                <th className="text-left px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Letter</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Sent</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Delivered</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Open Rate</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Click Rate</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">CTR</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Bounce</th>
                <th className="text-right px-4 py-2 uppercase tracking-[0.16em] text-[10px] text-neutral-500 font-normal">Unsub</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/60">
              {per_step.map((s) => (
                <tr key={s.step_id} className={s.is_active ? '' : 'opacity-50'}>
                  <td className="px-4 py-2 text-[#C8A15A] tabular-nums">{String(s.order).padStart(2, '0')}</td>
                  <td className="px-4 py-2">
                    <div className="text-neutral-900 truncate max-w-[240px]">{s.title}</div>
                    <div className="text-neutral-500 text-[11px] truncate max-w-[240px]">{s.subject || '—'}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-900">{s.sent}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-700">{s.delivered}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-900">{s.open_rate}%</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-900">{s.click_rate}%</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-700">{s.ctr_rate}%</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-500">{s.bounce_rate}%</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-500">{s.unsubscribe_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-step bar chart: opens & clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartByStep
          title="Opens by Letter"
          data={per_step.map((s) => ({ order: s.order, label: s.title, value: s.opened }))}
          max={maxOpens}
          barColor="#111"
        />
        <ChartByStep
          title="Clicks by Letter"
          data={per_step.map((s) => ({ order: s.order, label: s.title, value: s.clicked }))}
          max={maxClicks}
          barColor="#C8A15A"
        />
      </div>

      {/* 30-day time series */}
      <TimeSeriesChart series={time_series} />
    </div>
  );
}

function ChartByStep({ title, data, max, barColor }) {
  return (
    <div className="bg-white border border-neutral-200/60 rounded-xl p-5">
      <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-4">{title}</div>
      <div className="space-y-2">
        {data.map((d) => {
          const pct = max > 0 ? (d.value / max) * 100 : 0;
          return (
            <div key={d.order} className="flex items-center gap-3">
              <div className="w-6 text-[10px] tabular-nums text-neutral-500 text-right">{String(d.order).padStart(2, '0')}</div>
              <div className="flex-1 h-5 bg-neutral-100 rounded-full overflow-hidden relative">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-all"
                  style={{ width: `${Math.max(pct, d.value > 0 ? 4 : 0)}%`, background: barColor }}
                />
              </div>
              <div className="w-10 text-right text-[11px] tabular-nums text-neutral-700">{d.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeSeriesChart({ series }) {
  const width = 800;
  const height = 180;
  const padX = 32;
  const padY = 20;
  const w = width - padX * 2;
  const h = height - padY * 2;
  const maxVal = Math.max(1, ...series.map((p) => Math.max(p.opens, p.clicks)));
  const n = series.length || 1;
  const stepX = w / Math.max(1, n - 1);

  const line = (key, color) => {
    const pts = series.map((p, i) => `${padX + i * stepX},${padY + h - (p[key] / maxVal) * h}`).join(' ');
    return <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
  };

  const totalOpens = series.reduce((a, b) => a + b.opens, 0);
  const totalClicks = series.reduce((a, b) => a + b.clicks, 0);
  const firstLabel = series[0]?.day;
  const lastLabel = series[series.length - 1]?.day;

  return (
    <div className="bg-white border border-neutral-200/60 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Opens &amp; Clicks · Last 30 Days</div>
        <div className="flex gap-4 text-[11px]">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-neutral-900" /> Opens · {totalOpens}</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C8A15A]" /> Clicks · {totalClicks}</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
        {/* Grid line */}
        <line x1={padX} y1={padY + h} x2={padX + w} y2={padY + h} stroke="#eae4d8" />
        {line('opens', '#111')}
        {line('clicks', '#C8A15A')}
      </svg>
      <div className="flex justify-between mt-1 text-[10px] text-neutral-500 tabular-nums">
        <span>{firstLabel}</span>
        <span>{lastLabel}</span>
      </div>
    </div>
  );
}

function JourneyDetailModal({ detail, steps, onClose }) {
  const { journey, sends, subscriber } = detail;
  const sentByStepId = new Map((sends || []).map((s) => [s.step_id, s]));
  return (
    <div className="fixed inset-0 z-[60] bg-neutral-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="journey-detail-modal"
        className="bg-white rounded-2xl max-w-[720px] w-full max-h-[85vh] overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-neutral-200/60 px-6 py-4 flex justify-between items-start">
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-1">Subscriber Journey</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900">
              {subscriber?.first_name || journey.first_name || '—'} {subscriber?.last_name || ''}
            </div>
            <div className="text-[13px] text-neutral-500 mt-1">{subscriber?.email || journey.email}</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-2xl px-2" aria-label="Close">×</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <MetaCard label="Status" value={journey.status} />
            <MetaCard label="Source" value={subscriber?.source || '—'} />
            <MetaCard label="Subscribed" value={subscriber?.subscribed_at ? new Date(subscriber.subscribed_at).toLocaleDateString() : '—'} />
            <MetaCard label="Started" value={journey.started_at ? new Date(journey.started_at).toLocaleDateString() : '—'} />
            <MetaCard label="Next Send" value={journey.next_send_at ? new Date(journey.next_send_at).toLocaleString() : '—'} />
            <MetaCard label="Progress" value={`${journey.sent_step_ids?.length || 0} of ${steps.length}`} />
          </div>

          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">Sequence Timeline</div>
            <div className="space-y-2">
              {steps.map((s) => {
                const send = sentByStepId.get(s.id);
                const sent = !!send;
                return (
                  <div key={s.id} className={`flex items-start gap-3 p-3 rounded-lg border ${sent ? 'border-neutral-200 bg-white' : 'border-dashed border-neutral-200/70 bg-neutral-50/40'}`}>
                    <div className="w-8 shrink-0 text-center">
                      <div className={`w-6 h-6 rounded-full mx-auto flex items-center justify-center text-[10px] font-medium ${sent ? 'bg-neutral-900 text-white' : 'bg-neutral-200 text-neutral-500'}`}>
                        {sent ? '✓' : s.order}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-neutral-900 truncate">{s.title}</div>
                      <div className="text-[11px] text-neutral-500 truncate">{s.subject || '—'}</div>
                      {sent && (
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500">
                          <span>Sent · {new Date(send.sent_at).toLocaleString()}</span>
                          {send.delivered_at && <span className="text-emerald-700">Delivered</span>}
                          {send.first_open_at && <span className="text-neutral-900">Opened {send.open_count > 1 ? `× ${send.open_count}` : ''}</span>}
                          {send.first_click_at && <span className="text-[#C8A15A]">Clicked {send.click_count > 1 ? `× ${send.click_count}` : ''}</span>}
                          {send.bounced_at && <span className="text-red-600">Bounced</span>}
                          {send.unsubscribed_at && <span className="text-red-600">Unsubscribed</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaCard({ label, value }) {
  return (
    <div className="bg-neutral-50/70 border border-neutral-200/50 rounded-lg px-3 py-2.5">
      <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{label}</div>
      <div className="text-[12px] text-neutral-900 capitalize truncate">{value || '—'}</div>
    </div>
  );
}
